import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface GuideInsights {
  totalNursingHomes: string;
  averageWaitWeeks: string;
  fairDealApprovalRate: string;
  averageWeeklyCost: string;
  hiqa_registered: string;
  keyFact2026: string;
  topCountiesByAvailability: string[];
  questionsToAsk: string[];
}

const CACHE_KEY = "grandpal_guide_insights_v1";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function isValid(d: unknown): d is GuideInsights {
  if (!d || typeof d !== "object") return false;
  const obj = d as Record<string, unknown>;
  return typeof obj.totalNursingHomes === "string" && typeof obj.averageWeeklyCost === "string";
}

export function useGuideInsights() {
  const [insights, setInsights] = useState<GuideInsights | null>(null);
  const [citations, setCitations] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL_MS && isValid(data.insights)) {
          setInsights(data.insights);
          setCitations(data.citations || []);
          setIsLoading(false);
          return;
        }
        localStorage.removeItem(CACHE_KEY);
      }
    } catch (_) {
      localStorage.removeItem(CACHE_KEY);
    }

    const fetch = async () => {
      try {
        const { data, error: fnErr } = await supabase.functions.invoke("nursing-home-guide-insights");
        if (fnErr) throw new Error(fnErr.message);
        if (isValid(data.insights)) {
          setInsights(data.insights);
          setCitations(data.citations || []);
          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
          } catch (_) {}
        } else {
          throw new Error("Invalid data");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load");
      } finally {
        setIsLoading(false);
      }
    };

    fetch();
  }, []);

  return { insights, citations, isLoading, error };
}
